import { Component, ElementRef, Input, OnDestroy, AfterViewInit, SimpleChanges, OnChanges, NgZone } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min';
import { LocalService } from './local.service';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

export type NcCameraPlacementType = 'default' | 'left' | 'right' | 'top' | 'bottom' | 'back' | 'front';
export interface NcObjInfo {
	objUrl: string;
	mtlUrl: string;
}

@Component({
	selector: 'nc-obj-viewer',
	templateUrl: './obj-viewer.component.html',
	styleUrls: ['./obj-viewer.component.less']
})
export class ObjViewerComponent implements AfterViewInit, OnDestroy, OnChanges {
	/** 模型地址 */
	@Input() ncObjInfo!: NcObjInfo;
	/** 摄像机初始方位 */
	@Input() ncCameraPlacement: NcCameraPlacementType = 'default';
	/** 是否启用背景 */
	@Input() set ncBg(value: boolean | string) {
		this.state.background = value !== null && value !== undefined && value !== false && value !== 'false';
	}
	/** 背景色值 */
	@Input() set ncBgColor(value: string) {
		this.state.backgroundColor = value;
	}
	/** 背景图片URL */
	private _bgUrl: string = '';
	@Input() set ncBgUrl(value: string) {
		this._bgUrl = value;
		this.state.useBgUrl = value ? true : false;//传了值，默认启用背景图
		this.state.useBgSphere = value ? true : false;//传了值，默认启用背景球
	}
	get ncBgUrl(): string {
		return this._bgUrl;
	}
	/** 操作提示 */
	private _tips: boolean = false;
	@Input() set ncTips(value: boolean | string) {
		this._tips = value !== null && value !== undefined && value !== false && value !== 'false';
	}
	get ncTips(): boolean {
		return this._tips;
	}
	/** 控制面板 */
	private _control: boolean = false;
	@Input() set ncControl(value: boolean | string) {
		this._control = value !== null && value !== undefined && value !== false && value !== 'false';
	}
	get ncControl(): boolean {
		return this._control;
	}
	/** 背景是否透明 */
	private _alpha: boolean = false;
	@Input() set ncAlpha(value: boolean | string) {
		this._alpha = value !== null && value !== undefined && value !== false && value !== 'false';
	}
	get ncAlpha(): boolean {
		return this._alpha;
	}
	/** 自动旋转 */
	@Input() set ncAutoRotate(value: boolean | string) {
		this.state.autoRotate = value !== null && value !== undefined && value !== false && value !== 'false';
	}
	/** 自动旋转速度 */
	@Input() set ncAutoRotateSpeed(value: number) {
		this.state.autoRotateSpeed = value;
	}

	showLoading: boolean = false;
	loadingText: string = '';
	loadingText2: string = '';
	domId = '#objViewer';
	camera: any;
	cameraPlacement: string = 'default';
	backgroundSphere: any;
	scene: any;
	renderer: any;
	domWidth: any;
	domHeight: any;
	objScene: any;
	size: number = 0;
	center: any;
	controls: any;

	gui: any;
	state: any = {
		toneMapping: THREE.LinearToneMapping,
		toneMappingExposure: 0,
		background: false,
		backgroundColor: '#191919',
		useBgUrl: false,
		useBgSphere: false,
		axesHelper: false,
		wireframe: false,
		autoRotate: false,
		autoRotateSpeed: 0.5,
		ambientLight: true,
		ambientLightColor: '#FFFFFF',
		ambientLightIntensity: 2,
		directionalLight: true,
		directionalLightColor: '#FFFFFF',
		directionalLightIntensity: 2,
		directionalLightPosition: {
			x: 0,
			y: 0,
			z: 0
		},
		directionalLightHelper: false,
		directionalLight2: true,
		directionalLightColor2: '#FFFFFF',
		directionalLightIntensity2: 2,
		directionalLightPosition2: {
			x: 0,
			y: 0,
			z: 0
		},
		directionalLightHelper2: false,
		pointLight: false,
		pointLightColor: '#FFFFFF',
		pointLightIntensity: 1,
		pointLightDistance: 0,
		pointLightDecay: 0.1,
		pointLightPosition: {
			x: 0,
			y: 0,
			z: 0
		},
		pointLightHelper: false
	}

	constructor(private eleRef: ElementRef, private ls: LocalService, private ngZone: NgZone) { }

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['ncObjInfo'] && !changes['ncObjInfo'].firstChange) {
			this.updateModel();
		}
		if (changes['ncCameraPlacement'] && !changes['ncCameraPlacement'].firstChange) {
			this.setCameraPlacement();
		}
		if (changes['ncBg'] && !changes['ncBg'].firstChange
			|| changes['ncBgColor'] && !changes['ncBgColor'].firstChange
			|| changes['ncBgUrl'] && !changes['ncBgUrl'].firstChange) {
			this.updateBackground();
		}
		if (changes['ncAutoRotate'] && !changes['ncAutoRotate'].firstChange
			|| changes['ncAutoRotateSpeed'] && !changes['ncAutoRotateSpeed'].firstChange) {
			this.toggleAutoRotate();
		}
	}

	ngAfterViewInit(): void {
		this.onWindowResize = this.onWindowResize.bind(this);
		this.animate = this.animate.bind(this);
		if (this.ls.isBrowser) {
			this.ngZone.runOutsideAngular(() => {
				window.addEventListener('resize', this.onWindowResize);
			});
		}
		setTimeout(() => {
			this.ngZone.runOutsideAngular(() => {
				this.init();
			});
		})
	}

	ngOnDestroy(): void {
		if (this.ls.isBrowser) {
			window.removeEventListener('resize', this.onWindowResize);
		}
		this.webglLoseContext();
	}

	init() {
		let objArea: any = this.eleRef.nativeElement.querySelector(this.domId);
		this.domWidth = objArea.offsetWidth;
		this.domHeight = objArea.offsetHeight;

		this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: this.ncAlpha });
		this.renderer.setPixelRatio(objArea.devicePixelRatio);
		this.renderer.setSize(this.domWidth, this.domHeight);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		objArea.appendChild(this.renderer.domElement);

		this.scene = new THREE.Scene();
		const environment = new RoomEnvironment();
		const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
		this.scene.environment = pmremGenerator.fromScene(environment).texture;

		this.updateModel();
	}

	render() {
		this.renderer.render(this.scene, this.camera);
	}

	animate() {
		requestAnimationFrame(this.animate);
		// required if controls.enableDamping or controls.autoRotate are set to true
		if (this.controls) {
			this.controls.update();
		}
		if (this.scene && this.camera) {
			this.renderer.render(this.scene, this.camera);
		}
	}

	onWindowResize() {
		setTimeout(() => {
			let objArea: any = this.eleRef.nativeElement.querySelector(this.domId);
			const width = objArea.offsetWidth;
			const height = objArea.offsetHeight;
			this.renderer.setPixelRatio(objArea.devicePixelRatio);
			this.renderer.setSize(width, height);
			this.render();
		})
	}

	//更新模型
	updateModel() {
		if (!this.ncObjInfo.objUrl || !this.ncObjInfo.mtlUrl) {
			return;
		}

		this.showLoading = true;
		this.loadingText = '模型加载中...';
		this.loadingText2 = '';

		if (this.objScene) {
			this.scene.remove(this.objScene);
			this.objScene = null;
		}

		let baseUrl = this.ncObjInfo.objUrl.split('/').slice(0, -1).join('/');

		// 创建新的LoadingManager来重置进度计数
		const manager = new THREE.LoadingManager();
		manager.onProgress = (url: string, loaded: number, total: number) => {
			this.loadingText = `正在加载资源（${loaded}/${total}）`;
			if (loaded == total) {
				this.showLoading = false;
			}
		};

		new MTLLoader(manager).load(this.ncObjInfo.mtlUrl, (MaterialCreator: any) => {
			// 在preload()之前先处理材质信息，如果有贴图，则替换为obj同目录下的图片
			for (let name in MaterialCreator.materialsInfo) {
				let materialInfo = MaterialCreator.materialsInfo[name];
				// 需要处理的贴图类型
				const mapTypes = [
					'map_kd',     // 漫反射贴图
					'map_ks',     // 高光贴图  
					'map_ka',     // 环境光贴图
					'map_d',      // 透明度贴图
					'map_bump',   // 凹凸贴图
					'bump',       // 凹凸贴图
					'disp',       // 位移贴图
					'decal'       // 贴花贴图
				];
				mapTypes.map((type: string) => {
					if (materialInfo.hasOwnProperty(type)) {
						let map_type_url = materialInfo[type];
						let map_type_name = map_type_url.split(/[\/\\]/).pop();
						materialInfo[type] = baseUrl + '/' + map_type_name;
					}
				})
			}

			MaterialCreator.preload();

			new OBJLoader(manager).setMaterials(MaterialCreator).load(this.ncObjInfo.objUrl, (object: any) => {
				this.objScene = object;

				const box = new THREE.Box3().setFromObject(this.objScene);
				this.size = box.getSize(new THREE.Vector3()).length();
				this.center = box.getCenter(new THREE.Vector3());

				//将坐标系原点设置在模型中心
				this.objScene.position.x -= this.center.x;
				this.objScene.position.y -= this.center.y;
				this.objScene.position.z -= this.center.z;

				//PerspectiveCamera( fov : Number, aspect : Number, near : Number, far : Number )
				// fov — 摄像机视锥体垂直视野角度，眼睛张开的角度，0闭眼
				// aspect — 摄像机视锥体长宽比
				// near — 摄像机视锥体近端面，能看到的最近垂直距离
				// far — 摄像机视锥体远端面，能看到的最远垂直距离
				this.camera = new THREE.PerspectiveCamera(50, this.domWidth / this.domHeight, this.size / 100, this.size * 100);
				this.camera.updateProjectionMatrix();
				//position-摄像机位置
				//lookAt-摄像机望向位置  
				this.setCameraPlacement();

				//controls
				this.controls = new OrbitControls(this.camera, this.renderer.domElement);

				//根据模型size初始化state部分参数
				this.state.directionalLightPosition.x = this.size;
				this.state.directionalLightPosition.y = this.size / 2;
				this.state.directionalLightPosition.z = this.size;
				this.state.directionalLightPosition2.x = -this.size;
				this.state.directionalLightPosition2.y = -this.size / 2;
				this.state.directionalLightPosition2.z = -this.size;
				this.state.pointLightPosition.x = this.size / 2;
				this.state.pointLightPosition.y = 0;
				this.state.pointLightPosition.z = 0;
				this.state.pointLightDistance = this.size;

				//添加默认光源(添加至camera，光源固定位置，不随场景旋转)
				// this.scene.add(this.camera);
				// let defaultLight = new THREE.DirectionalLight('#FFFFFF', 1.5);
				// defaultLight.position.set(0, 0, this.size / 2);
				// this.camera.add(defaultLight);

				//添加GUI控制面板
				if (this.ncControl) {
					this.addGUI();
				} else {
					if (this.gui) {
						this.gui.destroy();
						this.gui = null;
					}
				}

				//根据state参数初始化相关设置
				this.updateToneMapping();
				this.updateToneMappingExposure();
				this.updateBackground();
				this.toggleAxesHelper();
				this.toggleWireframe();
				this.toggleAutoRotate();
				this.toggleAmbientLight();
				// this.updateAmbientLight();
				this.toggleDirectionalLight();
				// this.updateDirectionalLight();
				this.toggleDirectionalLightHelper();
				this.toggleDirectionalLight2();
				// this.updateDirectionalLight2();
				this.toggleDirectionalLightHelper2();
				this.togglePointLight();
				// this.updatePointLight();
				this.togglePointLightHelper();

				this.scene.add(this.objScene);
				this.animate();
			}, xhr => {
				if (xhr.loaded != xhr.total) {
					this.loadingText2 = '模型下载 ' + (xhr.loaded / xhr.total * 100).toFixed(0) + ' %';
				} else {
					this.loadingText2 = '';
				}
			});
		});
	}

	//设置相机角度和望向位置
	setCameraPlacement() {
		switch (this.cameraPlacement) {
			case 'left'://x-红
				this.camera.position.set(-this.size, 0, 0);
				this.camera.lookAt(0, 0, 0);
				break;
			case 'right'://x-红
				this.camera.position.set(this.size, 0, 0);
				this.camera.lookAt(0, 0, 0);
				break;
			case 'top'://y-绿
				this.camera.position.set(0, this.size, 0);
				this.camera.lookAt(0, 0, 0);
				break;
			case 'bottom'://y-绿
				this.camera.position.set(0, -this.size, 0);
				this.camera.lookAt(0, 0, 0);
				break;
			case 'front'://z-蓝
				this.camera.position.set(0, 0, this.size);
				this.camera.lookAt(0, 0, 0);
				break;
			case 'back'://z-蓝
				this.camera.position.set(0, 0, -this.size);
				this.camera.lookAt(0, 0, 0);
				break;
			default:
				this.camera.position.set(this.size / 1.3, this.size / 1.5, this.size / 1.3);
				this.camera.lookAt(0, 0, 0);
				break;
		}
	}

	//add GUI
	addGUI() {
		//折叠参数
		let closeState: any = {
			gui: false,
			displayFolder: false,
			ambientLightFolder: true,
			directionalLightFolder: true,
			directionalLightHelperFolder: false,
			directionalLightFolder2: true,
			directionalLightHelperFolder2: false,
			pointLightFolder: true,
			pointlLightHelperFolder: false
		}
		if (this.gui) {
			//记录上个gui的面板折叠值
			closeState = {
				gui: this.gui._closed,
				displayFolder: this.gui.children[0]._closed,
				ambientLightFolder: this.gui.children[1]._closed,
				directionalLightFolder: this.gui.children[2]._closed,
				directionalLightHelperFolder: this.gui.children[2].children[3]._closed,
				directionalLightFolder2: this.gui.children[3]._closed,
				directionalLightHelperFolder2: this.gui.children[3].children[3]._closed,
				pointLightFolder: this.gui.children[4]._closed,
				pointlLightHelperFolder: this.gui.children[4].children[5]._closed
			}
			this.gui.destroy();
			this.gui = null;
		}
		this.gui = new GUI({ title: '控制面板', container: this.eleRef.nativeElement.querySelector('#control') });
		if (closeState.gui) {
			this.gui.close();
		}
		const displayFolder = this.gui.addFolder('显示设置');
		if (closeState.displayFolder) {
			displayFolder.close();
		}
		displayFolder.add(this.state, 'toneMapping',
			{
				'ACESFilmic': THREE.ACESFilmicToneMapping,
				'Linear': THREE.LinearToneMapping,
				'Reinhard': THREE.ReinhardToneMapping,
				'Cineon': THREE.CineonToneMapping,
				'AgX': THREE.AgXToneMapping,
				'Neutral': THREE.NeutralToneMapping
			}
		).name('色调').onChange(() => this.updateToneMapping());
		displayFolder.add(this.state, 'toneMappingExposure', -10, 5, 0.01).name('曝光').onChange(() => this.updateToneMappingExposure());
		displayFolder.add(this.state, 'background').name('启用场景背景').listen().onChange(() => this.updateBackground());
		displayFolder.addColor(this.state, 'backgroundColor').name('场景背景色').listen().onChange(() => this.updateBackground());
		//传入背景图才显示背景图设置
		if (this.ncBgUrl) {
			displayFolder.add(this.state, 'useBgUrl').name('使用背景贴图').listen().onChange(() => this.updateBackground());
			displayFolder.add(this.state, 'useBgSphere').name('球形背景贴图').listen().onChange(() => this.updateBackground());
		}
		displayFolder.add(this.state, 'axesHelper').name('坐标系').onChange(() => this.toggleAxesHelper());
		displayFolder.add(this.state, 'wireframe').name('线框模式').onChange(() => this.toggleWireframe());
		displayFolder.add(this.state, 'autoRotate').listen().name('自动旋转').onChange(() => this.toggleAutoRotate());
		displayFolder.add(this.state, 'autoRotateSpeed', -10.0, 10.0, 0.1).listen().name('旋转速度').onChange(() => this.toggleAutoRotate());

		const ambientLightFolder = this.gui.addFolder('环境光');
		if (closeState.ambientLightFolder) {
			ambientLightFolder.close();
		}
		ambientLightFolder.add(this.state, 'ambientLight').name('启用').onChange(() => this.toggleAmbientLight());
		ambientLightFolder.addColor(this.state, 'ambientLightColor').name('颜色').onChange(() => this.updateAmbientLight());
		ambientLightFolder.add(this.state, 'ambientLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updateAmbientLight());

		const directionalLightFolder = this.gui.addFolder('定向光1');
		if (closeState.directionalLightFolder) {
			directionalLightFolder.close();
		}
		directionalLightFolder.add(this.state, 'directionalLight').name('启用').onChange(() => this.toggleDirectionalLight());
		directionalLightFolder.addColor(this.state, 'directionalLightColor').name('颜色').onChange(() => this.updateDirectionalLight());
		directionalLightFolder.add(this.state, 'directionalLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updateDirectionalLight());
		const directionalLightHelperFolder = directionalLightFolder.addFolder('定向光位置');
		if (closeState.directionalLightHelperFolder) {
			directionalLightHelperFolder.close();
		}
		directionalLightHelperFolder.add(this.state, 'directionalLightHelper').listen().name('位置模拟').onChange(() => this.toggleDirectionalLightHelper());
		directionalLightHelperFolder.add(this.state.directionalLightPosition, 'x', -this.size, this.size).name('x（坐标系-红）').decimals(2).onChange(() => this.updateDirectionalLight());
		directionalLightHelperFolder.add(this.state.directionalLightPosition, 'y', -this.size, this.size).name('y（坐标系-绿）').decimals(2).onChange(() => this.updateDirectionalLight());
		directionalLightHelperFolder.add(this.state.directionalLightPosition, 'z', -this.size, this.size).name('z（坐标系-蓝）').decimals(2).onChange(() => this.updateDirectionalLight());

		const directionalLightFolder2 = this.gui.addFolder('定向光2');
		if (closeState.directionalLightFolder2) {
			directionalLightFolder2.close();
		}
		directionalLightFolder2.add(this.state, 'directionalLight2').name('启用').onChange(() => this.toggleDirectionalLight2());
		directionalLightFolder2.addColor(this.state, 'directionalLightColor2').name('颜色').onChange(() => this.updateDirectionalLight2());
		directionalLightFolder2.add(this.state, 'directionalLightIntensity2', 0, 10, 0.1).name('强度').onChange(() => this.updateDirectionalLight2());
		const directionalLightHelperFolder2 = directionalLightFolder2.addFolder('定向光位置');
		if (closeState.directionalLightHelperFolder2) {
			directionalLightHelperFolder2.close();
		}
		directionalLightHelperFolder2.add(this.state, 'directionalLightHelper2').listen().name('位置模拟').onChange(() => this.toggleDirectionalLightHelper2());
		directionalLightHelperFolder2.add(this.state.directionalLightPosition2, 'x', -this.size, this.size).name('x（坐标系-红）').decimals(2).onChange(() => this.updateDirectionalLight2());
		directionalLightHelperFolder2.add(this.state.directionalLightPosition2, 'y', -this.size, this.size).name('y（坐标系-绿）').decimals(2).onChange(() => this.updateDirectionalLight2());
		directionalLightHelperFolder2.add(this.state.directionalLightPosition2, 'z', -this.size, this.size).name('z（坐标系-蓝）').decimals(2).onChange(() => this.updateDirectionalLight2());

		const pointLightFolder = this.gui.addFolder('点光源');
		if (closeState.pointLightFolder) {
			pointLightFolder.close();
		}
		pointLightFolder.add(this.state, 'pointLight').name('启用').onChange(() => this.togglePointLight());
		pointLightFolder.addColor(this.state, 'pointLightColor').name('颜色').onChange(() => this.updatePointLight());
		pointLightFolder.add(this.state, 'pointLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updatePointLight());
		pointLightFolder.add(this.state, 'pointLightDistance', 0, this.size).name('范围').decimals(2).onChange(() => this.updatePointLight());
		pointLightFolder.add(this.state, 'pointLightDecay', 0, 2, 0.1).name('衰减').onChange(() => this.updatePointLight());
		const pointlLightHelperFolder = pointLightFolder.addFolder('点光源位置');
		if (closeState.pointlLightHelperFolder) {
			pointlLightHelperFolder.close();
		}
		pointlLightHelperFolder.add(this.state, 'pointLightHelper').listen().name('位置模拟').onChange(() => this.togglePointLightHelper());
		pointlLightHelperFolder.add(this.state.pointLightPosition, 'x', -this.size, this.size).name('x（坐标系-红）').decimals(2).onChange(() => this.updatePointLight());
		pointlLightHelperFolder.add(this.state.pointLightPosition, 'y', -this.size, this.size).name('y（坐标系-绿）').decimals(2).onChange(() => this.updatePointLight());
		pointlLightHelperFolder.add(this.state.pointLightPosition, 'z', -this.size, this.size).name('z（坐标系-蓝）').decimals(2).onChange(() => this.updatePointLight());
	}

	//色调
	updateToneMapping() {
		this.renderer.toneMapping = this.state.toneMapping;
	}

	//曝光
	updateToneMappingExposure() {
		this.renderer.toneMappingExposure = Math.pow(2, this.state.toneMappingExposure);
	}

	//背景
	updateBackground() {
		// 移除已存在的背景球体
		if (this.backgroundSphere) {
			this.scene.remove(this.backgroundSphere);
			this.backgroundSphere = null;
		}

		//启用背景
		if (this.state.background) {
			//使用背景图
			if (this.state.useBgUrl && this.ncBgUrl) {
				//使用球体背景
				if (this.state.useBgSphere) {
					// 创建球形几何体
					const geometry = new THREE.SphereGeometry(this.size * 50, 32, 32);
					// 将法线朝内,这样纹理会显示在球体内侧
					geometry.scale(-1, 1, 1);

					// 加载背景图纹理
					new THREE.TextureLoader().load(this.ncBgUrl, (texture: any) => {
						texture.colorSpace = THREE.SRGBColorSpace;
						const material = new THREE.MeshBasicMaterial({
							map: texture
						});
						this.backgroundSphere = new THREE.Mesh(geometry, material);
						this.scene.add(this.backgroundSphere);
					});
				} else {
					//使用平面背景图
					new THREE.TextureLoader().load(this.ncBgUrl, (texture: any) => {
						texture.colorSpace = THREE.SRGBColorSpace;
						this.scene.background = texture;
					});
				}
			} else {
				//使用背景色
				this.scene.background = new THREE.Color(this.state.backgroundColor);
			}
		} else {
			//关闭背景
			this.scene.background = null;
		}
	}

	//开关辅助观察坐标系
	axesHelper: any;
	toggleAxesHelper() {
		//x-红，y-绿，z-蓝
		if (this.state.axesHelper) {
			if (this.axesHelper) {
				this.scene.remove(this.axesHelper);
				this.axesHelper = null;
			}
			this.axesHelper = new THREE.AxesHelper(this.size);
			this.scene.add(this.axesHelper);
		} else {
			if (this.axesHelper) {
				this.scene.remove(this.axesHelper);
				this.axesHelper = null;
			}
		}
	}

	//线框模式
	toggleWireframe() {
		this.traverseMaterials(this.objScene, (material: any) => {
			material.wireframe = this.state.wireframe;

			if (material instanceof THREE.PointsMaterial) {
				material.size = 1;
			}
		});
	}

	//遍历材质
	traverseMaterials(object: any, callback: any) {
		object.traverse((node: any) => {
			if (!node.geometry) return;
			const materials = Array.isArray(node.material) ? node.material : [node.material];
			materials.forEach(callback);
		});
	}

	//自动旋转
	toggleAutoRotate() {
		this.controls.autoRotate = this.state.autoRotate;
		this.controls.autoRotateSpeed = this.state.autoRotateSpeed;
	}

	//开关环境光
	ambientLight: any;
	toggleAmbientLight() {
		if (this.state.ambientLight) {
			if (this.ambientLight) {
				this.scene.remove(this.ambientLight);
				this.ambientLight = null;
			}
			this.ambientLight = new THREE.AmbientLight(this.state.ambientLightColor, this.state.ambientLightIntensity);
			this.scene.add(this.ambientLight);
		} else {
			if (this.ambientLight) {
				this.scene.remove(this.ambientLight);
				this.ambientLight = null;
			}
		}
	}
	//更新环境光
	updateAmbientLight() {
		if (this.state.ambientLight) {
			this.ambientLight.color.set(this.state.ambientLightColor);
			this.ambientLight.intensity = this.state.ambientLightIntensity;
		}
	}

	//开关定向光
	directionalLight: any;
	toggleDirectionalLight() {
		if (this.state.directionalLight) {
			if (this.directionalLight) {
				this.scene.remove(this.directionalLight);
				this.directionalLight = null;
			}
			this.directionalLight = new THREE.DirectionalLight(this.state.directionalLightColor, this.state.directionalLightIntensity);
			this.directionalLight.position.set(
				this.state.directionalLightPosition.x,
				this.state.directionalLightPosition.y,
				this.state.directionalLightPosition.z
			);
			this.scene.add(this.directionalLight);
		} else {
			if (this.directionalLight) {
				this.scene.remove(this.directionalLight);
				this.directionalLight = null;
			}
			//关闭位置模拟
			this.state.directionalLightHelper = false;
			this.toggleDirectionalLightHelper();
		}
	}
	//更新定向光
	updateDirectionalLight() {
		if (this.state.directionalLight) {
			this.directionalLight.color.set(this.state.directionalLightColor);
			this.directionalLight.intensity = this.state.directionalLightIntensity;
			this.directionalLight.position.set(
				this.state.directionalLightPosition.x,
				this.state.directionalLightPosition.y,
				this.state.directionalLightPosition.z
			);
			if (this.directionalLightHelper) {
				this.directionalLightHelper.update()
			}
		}
	}
	//开关定向光位置模拟
	directionalLightHelper: any;
	toggleDirectionalLightHelper() {
		if (this.state.directionalLightHelper) {
			if (this.directionalLight) {//开启定向光才能开启位置模拟
				if (this.directionalLightHelper) {
					this.scene.remove(this.directionalLightHelper);
					this.directionalLightHelper = null;
				}
				this.directionalLightHelper = new THREE.DirectionalLightHelper(this.directionalLight, this.size / 20, 0xff0000);
				this.scene.add(this.directionalLightHelper);
			} else {
				this.state.directionalLightHelper = false;
			}
		} else {
			if (this.directionalLightHelper) {
				this.scene.remove(this.directionalLightHelper);
				this.directionalLightHelper = null;
			}
		}
	}

	//开关定向光2
	directionalLight2: any;
	toggleDirectionalLight2() {
		if (this.state.directionalLight2) {
			if (this.directionalLight2) {
				this.scene.remove(this.directionalLight2);
				this.directionalLight2 = null;
			}
			this.directionalLight2 = new THREE.DirectionalLight(this.state.directionalLightColor2, this.state.directionalLightIntensity2);
			this.directionalLight2.position.set(
				this.state.directionalLightPosition2.x,
				this.state.directionalLightPosition2.y,
				this.state.directionalLightPosition2.z
			);
			this.scene.add(this.directionalLight2);
		} else {
			if (this.directionalLight2) {
				this.scene.remove(this.directionalLight2);
				this.directionalLight2 = null;
			}
			//关闭位置模拟
			this.state.directionalLightHelper2 = false;
			this.toggleDirectionalLightHelper2();
		}
	}
	//更新定向光2
	updateDirectionalLight2() {
		if (this.state.directionalLight2) {
			this.directionalLight2.color.set(this.state.directionalLightColor2);
			this.directionalLight2.intensity = this.state.directionalLightIntensity2;
			this.directionalLight2.position.set(
				this.state.directionalLightPosition2.x,
				this.state.directionalLightPosition2.y,
				this.state.directionalLightPosition2.z
			);
			if (this.directionalLightHelper2) {
				this.directionalLightHelper2.update()
			}
		}
	}
	//开关定向光2位置模拟
	directionalLightHelper2: any;
	toggleDirectionalLightHelper2() {
		if (this.state.directionalLightHelper2) {
			if (this.directionalLight2) {//开启定向光才能开启位置模拟
				if (this.directionalLightHelper2) {
					this.scene.remove(this.directionalLightHelper2);
					this.directionalLightHelper2 = null;
				}
				this.directionalLightHelper2 = new THREE.DirectionalLightHelper(this.directionalLight2, this.size / 20, 0xffff00);
				this.scene.add(this.directionalLightHelper2);
			} else {
				this.state.directionalLightHelper2 = false;
			}
		} else {
			if (this.directionalLightHelper2) {
				this.scene.remove(this.directionalLightHelper2);
				this.directionalLightHelper2 = null;
			}
		}
	}

	//开关点光源
	pointLight: any;
	togglePointLight() {
		if (this.state.pointLight) {
			if (this.pointLight) {
				this.scene.remove(this.pointLight);
				this.pointLight = null;
			}
			//PointLight( color : Integer, intensity : Float, distance : Number, decay : Float )
			this.pointLight = new THREE.PointLight(this.state.pointLightColor, this.state.pointLightIntensity, this.state.pointLightDistance, this.state.pointLightDecay);
			this.pointLight.position.set(
				this.state.pointLightPosition.x,
				this.state.pointLightPosition.y,
				this.state.pointLightPosition.z
			);
			this.scene.add(this.pointLight);
		} else {
			if (this.pointLight) {
				this.scene.remove(this.pointLight);
				this.pointLight = null;
			}
			//关闭位置模拟
			this.state.pointLightHelper = false;
			this.togglePointLightHelper();
		}
	}

	//更新点光源
	updatePointLight() {
		if (this.state.pointLight) {
			this.pointLight.color.set(this.state.pointLightColor);
			this.pointLight.intensity = this.state.pointLightIntensity;
			this.pointLight.decay = this.state.pointLightDecay;
			this.pointLight.distance = this.state.pointLightDistance;
			this.pointLight.position.set(
				this.state.pointLightPosition.x,
				this.state.pointLightPosition.y,
				this.state.pointLightPosition.z
			);
			if (this.pointLightHelper) {
				this.pointLightHelper.update()
			}
		}
	}
	//开关点光源位置模拟
	pointLightHelper: any;
	togglePointLightHelper() {
		if (this.state.pointLightHelper) {
			if (this.pointLight) {//开启点光源才能开启位置模拟
				if (this.pointLightHelper) {
					this.scene.remove(this.pointLightHelper);
					this.pointLightHelper = null;
				}
				this.pointLightHelper = new THREE.PointLightHelper(this.pointLight, this.size / 20, 0x2bff00);
				this.scene.add(this.pointLightHelper);
			} else {
				this.state.pointLightHelper = false;
			}
		} else {
			if (this.pointLightHelper) {
				this.scene.remove(this.pointLightHelper);
				this.pointLightHelper = null;
			}
		}
	}

	//webgl销毁
	webglLoseContext() {
		let objCanvasDom: any = this.eleRef.nativeElement.querySelector(`${this.domId} canvas`);
		if (objCanvasDom) {
			//手动销毁WebGL context对象（最多16个，超出的话最先加载的会被移除）
			let gl = objCanvasDom.getContext('webgl2');
			gl.getExtension('WEBGL_lose_context').loseContext();
			//dom移除
			objCanvasDom.remove();
		}
	}

}
